import React, { useState } from "react";
import { Modal as RBModal, ModalProps as RBModalProps } from "react-bootstrap";
import styled from "styled-components/macro";

import CloseButton, { CloseButtonProps } from "components/CloseButton";
import Button, { UIButtonProps } from "ui/Button";
import Typography from "ui/Typography";

import { Text } from "@chanzuckerberg/eds";

export const ModalContext = React.createContext({
  inModal: false,
  hasEntered: false,
});

export interface ModalProps extends RBModalProps {
  /**
   * If set, a close button will be rendered
   */
  close?: CloseButtonProps;
  /**
   * We currently have only two standard modal sizes: the default size and "lg".
   */
  size?: "lg";
}

// Padding on each side of the modal content and between the modal and footer.
const INTERNAL_SPACING = "2rem";

const $ModalDialog = styled(RBModal.Dialog)`
  @media screen and (${({ theme }) => theme.BreakpointPhoneUp}) {
    max-width: 28rem;
    margin-left: auto;
    margin-right: auto;
  }

  &.modal-lg {
    @media screen and (${({ theme }) => theme.BreakpointLargeTabletUp}) {
      max-width: 50rem;
    }
  }

  .modal-content {
    border-radius: 0;
    // This invisible border is for Windows High Contrast Mode, which stripes decorative
    // colors from the page. That is a super useful tool, but it makes buttons and modals
    // that have no borders really hard to understand. But all text and borders are made
    // opaque black automatically, so an invisible border here will turn into a black
    // border there.
    border: 1px solid rgba(0, 0, 0, 0);
    padding: ${INTERNAL_SPACING};
  }
`;

const $CloseWrapper = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  // Ensure the close button is positioned above other absolutely positioned
  // elements in the modal.
  z-index: ${({ theme }) => theme.zIndexLevelTwo};
`;

const $CloseButton = styled(CloseButton)`
  padding: 0.625rem 0.6875rem;
`;

const $ModalImage = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
`;

const $ModalTitle = styled(Typography).attrs({
  size: "h3",
  forwardedAs: "h1",
})``;

// Original ModalSubtitle
// const $ModalSubtitle = styled(Typography).attrs({
//   size: "bodySansSerif",
// })`
//   margin-top: 1rem;
// `;

// Option 1: wrap EDS components with `styled`
const $ModalSubtitle = styled(Text).attrs({})`
  margin: 1rem 0 0;
  color: var(--eds-theme-color-text-neutral-strong);
`;

// Option 2: Replace component with EDS version (when no style overrides needed)
// const $ModalSubtitle = (props: { children: string | ReactNode }) => (
//   <Text as="span" {...props} />
// );

const $ModalHeader = styled.div`
  padding-top: 1rem;
  text-align: center;
`;

const $ModalBody = styled(RBModal.Body)`
  padding: 0;

  ${$ModalHeader} + & {
    padding-top: 1rem;
  }

  .modal-lg ${$ModalHeader} + & {
    border-top: 1px solid ${({ theme }) => theme.ColorSpace100};
    margin-top: 1.5rem;
    padding-top: 1.5rem;
  }
`;

const $ModalFooter = styled(RBModal.Footer)`
  border: 0;
  border-radius: 0;
  justify-content: center;
  padding: ${INTERNAL_SPACING} 0 0;

  // Bootstrap adds margin to all elements within the footer.
  & > * {
    margin: unset;
  }
`;

interface ModalButtonProps extends Omit<UIButtonProps, "requestInProgress"> {
  requestInProgress?: boolean;
}

const $ModalButton = styled(Button)<UIButtonProps>`
  min-width: 18.5rem;
`;

function ModalButton({
  block = true,
  requestInProgress = false,
  ...props
}: ModalButtonProps) {
  return (
    <$ModalButton
      block={block}
      requestInProgress={requestInProgress}
      {...props}
    />
  );
}

function PrimaryButton(props: ModalButtonProps) {
  return <ModalButton {...props} variant="primary" />;
}

function SecondaryButton(props: ModalButtonProps) {
  return <ModalButton {...props} variant="link" />;
}

function PrimaryButtonTransparent(props: ModalButtonProps) {
  return <ModalButton {...props} variant="secondary" />;
}

/**
 * Our modal uses the React Bootstrap Modal component with some styling
 * modifications. RB Modal documentation: https://react-bootstrap.github.io/components/modal/
 */
function Modal({
  centered = true,
  children,
  close,
  dialogAs = $ModalDialog,
  onEntered,
  ...props
}: ModalProps) {
  const [hasEntered, setHasEntered] = useState(false);

  return (
    <RBModal
      {...props}
      centered={centered}
      dialogAs={dialogAs}
      onHide={close ? close.onClick : () => {}}
      onEntered={(node: HTMLElement, isAppearing: boolean) => {
        setHasEntered(true);
        onEntered && onEntered(node, isAppearing);
      }}
    >
      {close && (
        <$CloseWrapper>
          <$CloseButton {...close} />
        </$CloseWrapper>
      )}
      <ModalContext.Provider value={{ inModal: true, hasEntered }}>
        {children}
      </ModalContext.Provider>
    </RBModal>
  );
}

Modal.Dialog = $ModalDialog;
Modal.Header = $ModalHeader;
Modal.Image = $ModalImage;
Modal.Title = $ModalTitle;
Modal.Subtitle = $ModalSubtitle;
Modal.Body = $ModalBody;
Modal.Footer = $ModalFooter;
Modal.PrimaryButton = PrimaryButton;
Modal.SecondaryButton = SecondaryButton;
Modal.PrimaryButtonTransparent = PrimaryButtonTransparent;

export default Modal;
